import { useEffect, useState } from "react"

import { getMapFeatures } from "../api/global-map.api.ts"
import type { EntityQuickView, MapFeature } from "../redux/global-map.types"

// Fixed for now, per explicit product direction ("will be configured
// through setting in the future") — no picker in this first version, unlike
// "Look Around a Zip Code"'s six-stop slider.
const NEARBY_RADIUS_MI = 10
// High enough that a tight, already-small (<=10mi radius) result set stays
// in `mode: "features"` rather than tipping into `cluster_features`'s
// zoom-threshold clustering branch — this list only ever wants individual
// rows, never an aggregated blob.
const NEARBY_ZOOM = 14

interface NearbyBreakdownCounts {
  project: number
  lead: number
  subcontractor: number
  appointment: number
}

interface UseNearbyFeaturesResult {
  status: "loading" | "ready" | "error" | "no-location"
  rows: MapFeature[]
  counts: NearbyBreakdownCounts | null
}

/**
 * The single fetch behind BOTH the always-visible Nearby breakdown (counts
 * only, shown under Overview and Files) and the dedicated Nearby tab (same
 * counts plus the filterable row list) — lifted out of `QuickViewNearby` so
 * `EntityQuickView` can call it once and hand the same result to both,
 * rather than each mounting its own copy of this effect and doubling the
 * request. See `QuickViewNearby`'s own former docstring (now here) for why
 * this is a raw, unbounded `getMapFeatures` call independent of the main
 * map's committed filters/viewport.
 */
function useNearbyFeatures(
  record: EntityQuickView | null
): UseNearbyFeaturesResult {
  const [status, setStatus] =
    useState<UseNearbyFeaturesResult["status"]>("loading")
  const [rows, setRows] = useState<MapFeature[]>([])
  const [counts, setCounts] = useState<NearbyBreakdownCounts | null>(null)

  const latitude = record?.location.latitude ?? null
  const longitude = record?.location.longitude ?? null
  const selfId = record
    ? `${record.entity_type === "project" ? "p" : "l"}:${record.id}`
    : null

  useEffect(() => {
    let cancelled = false

    if (!selfId || latitude == null || longitude == null) {
      setStatus("no-location")
      setRows([])
      setCounts(null)
      return
    }

    setStatus("loading")
    setRows([])
    setCounts(null)

    void getMapFeatures({
      zoom: NEARBY_ZOOM,
      entities: "project,lead",
      near_latitude: latitude,
      near_longitude: longitude,
      near_radius_mi: String(NEARBY_RADIUS_MI)
    }).then((response) => {
      if (cancelled) return
      if (!response.ok || !response.data || response.data.mode !== "features") {
        setStatus("error")
        return
      }
      // The record's own coordinate is what this call searches around, so
      // it's always within its own radius — excluded from the row list
      // (`selfId`) below, and, per the same "don't count yourself" rule,
      // subtracted from whichever total (`project`/`lead`) its own entity
      // type belongs to. Guarded on it actually being present in the raw
      // response rather than subtracted unconditionally, so a future
      // backend change that already excludes it server-side can never
      // under-count.
      const selfPresent = response.data.features.some(
        (feature) => feature.id === selfId
      )
      setRows(response.data.features.filter((feature) => feature.id !== selfId))
      // The same call's own KPI totals, radius-filtered by the identical
      // `near_latitude`/`near_longitude`/`near_radius_mi` params — no second
      // fetch needed. Subcontractors/Appointments have no row in `features`
      // at all (that array only ever carries Project/Lead pins), so the
      // breakdown is the only place their nearby counts show at all.
      // Guarded on every count actually being a number — never renders
      // "undefined" from a malformed/partial response.
      const {
        project_total,
        lead_total,
        subcontractor_total,
        appointment_total
      } = response.data
      if (
        typeof project_total === "number" &&
        typeof lead_total === "number" &&
        typeof subcontractor_total === "number" &&
        typeof appointment_total === "number"
      ) {
        // Read off `selfId` (already an effect dependency) rather than the
        // outer `record` prop directly, so this never reads a stale closure
        // if `record` changes identity without `selfId` itself changing.
        const selfIsProject = selfPresent && selfId.startsWith("p:")
        const selfIsLead = selfPresent && selfId.startsWith("l:")
        setCounts({
          project: project_total - (selfIsProject ? 1 : 0),
          lead: lead_total - (selfIsLead ? 1 : 0),
          subcontractor: subcontractor_total,
          appointment: appointment_total
        })
      }
      setStatus("ready")
    })

    return () => {
      cancelled = true
    }
  }, [latitude, longitude, selfId])

  return { status, rows, counts }
}

export { NEARBY_RADIUS_MI, useNearbyFeatures }
export type { NearbyBreakdownCounts, UseNearbyFeaturesResult }
